import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { format as formatDate } from 'date-fns';

import { CardContainer, CardContent, CardHeader, FormControlContainer, FormControlLabel, PageView, PageViewHeader } from '../../../../../components/Layout';
import Paginator from '../../../../../components/Paginator';
import { Table } from '../../../../../components/Table';
import { AppState } from '../../../../../store/RootReducer';
import { connect } from 'react-redux';
import LoadingPageView from '../../../../../components/LoadingPageView';
import ErrorPageView from '../../../../../components/ErrorPageView';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import { TriggerDisplayToastPayload } from '../../../../../components/toast/presentation/store/types';
import { triggerDisplayToast } from '../../../../../components/toast/presentation/store/actions';
import usePrevious from '../../../../../hooks/usePrevious';
import Modal from '../../../../../components/Modal';

import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { FinalityLog, GetFinalityLogsRequest, GetFinalityLogsResponse } from '../../../domain/repositories/FinalityRepository';
import { resetGetFinalityLogs, triggerGetFinalityLogs } from '../../store/getFinalityLogs/actions';
import TextField from '../../../../../components/TextField';
import DateField from '../../../../../components/DateField';
import Button from '../../../../../components/Button';
import DetailsModalContent from './DetailsModalContent';



const Container = styled(PageView)``;

const SearchFormContainer = styled(CardContainer)`
  margin-bottom: 3rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
`;

const ControlContainer = styled(FormControlContainer)`
  flex: 1;
  margin-bottom: 0px;
  margin-right: 20px;
  max-width: 200px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: 30px;

  & > *:first-child {
    margin-right: 20px;
  }
`;


const ContentContainer = styled(CardContainer)`
  padding: 0px;
`;

const TableContainer = styled.div`
  position: relative;
  overflow: auto;

  td a {
    white-space: nowrap;
  }

  td.owner-column {
    min-width: 150px;
  }

  td.actions-container a {
    margin-right: 30px;
  }

  td.actions-container a:last-child {
    margin-right: 0px;
  }


  td.actions-container a.delete-action {
    color: red;
  }
`;

const TableControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #ededed;
`;

const PageDetails = styled.div`
  margin-right: 40px;

  p {
    color: #00425F;
  }
`;

const PAGE_SIZE = 10;

interface RouteParams {
  officeServer: string;
}

interface StoreStateProps {
  getFinalityLogsPending: boolean;
  getFinalityLogsError: boolean;
  getFinalityLogsResponse: GetFinalityLogsResponse | null;
}

interface StoreDispatchProps {
  getFinalityLogs: (request: GetFinalityLogsRequest) => void;
  resetGetFinalityLogs: () => void;
  displayToast: (toastPayload: TriggerDisplayToastPayload) => void;
}

interface OwnProps {

}

type Props = RouteComponentProps<RouteParams> & StoreStateProps & StoreDispatchProps & OwnProps;

export function FinalityLogsPage(props: Props) {
  const {
    getFinalityLogsPending,
    getFinalityLogsError,
    getFinalityLogsResponse,

    getFinalityLogs,
    resetGetFinalityLogs,
    displayToast
  } = props;

  const prevGetFinalityLogsPending = usePrevious(getFinalityLogsPending);

  const [currentPage, setCurrentPage] = useState(1);
  const [updatedPage, setUpdatedPage] = useState(1);

  const [bankDetails, setbankDetails] = useState('');
  const [transactionDate, setTransactionDate] = useState<Date | null>(null);
  const [requestDate, setRequestDate] = useState<Date | null>(null);
  const [client, setClient] = useState('');


  const [detailsModalData, setDetailsModalData] = useState<FinalityLog>();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);


  const fetchFinalityLogs = (pageNumber?: number) => {
    if (getFinalityLogsPending) return;

    if (pageNumber) setUpdatedPage(pageNumber);

    const transactionDateParam = transactionDate ? formatDate(transactionDate, "yyyy-MM-dd") : undefined;
    const requestDateParam = requestDate ? formatDate(requestDate, "yyyy-MM-dd") : undefined;

    getFinalityLogs({
      pageSize: PAGE_SIZE,
      page: pageNumber || currentPage,
      bankDetails: bankDetails || undefined,
      transactionDate: transactionDateParam,
      requestDate: requestDateParam,
      client: client || undefined,
    });
  };

  const searchButtonHandler = () => {
    setCurrentPage(1);
    setUpdatedPage(1);

    resetGetFinalityLogs();

    fetchFinalityLogs(1);
  };

  const clearSearchButtonHandler = () => {
    // clear filter params
    setbankDetails('');
    setTransactionDate(null);
    setRequestDate(null);
    setClient('');

    setCurrentPage(1);
    setUpdatedPage(1);

    resetGetFinalityLogs();

    fetchFinalityLogs(1);
  }

  const viewFinalityLogDetails = (finalityLog: FinalityLog) => {
    setDetailsModalData(finalityLog);
    setIsDetailsModalOpen(true);
  };

  useEffect(() => {
    return () => resetGetFinalityLogs();
  }, []);

  useEffect(() => {
    if (!(prevGetFinalityLogsPending && !getFinalityLogsPending)) return;

    if (getFinalityLogsError) {
      displayToast({ toastType: "ERROR" });
      return;
    }

    setCurrentPage(updatedPage);
  }, [getFinalityLogsPending]);


  return (
    <Container>

      <Modal
        id="log-details-modal"
        open={isDetailsModalOpen}
        closeButtonHandler={() => setIsDetailsModalOpen(false)}
        modalTitle="Finality Log Details"
        wrapperStyle={{ marginTop: "100px" }}
      >
        <DetailsModalContent
          finalityLogDetails={detailsModalData}
        />
      </Modal>

      <PageViewHeader>
        <h3>Finality Logs</h3>
      </PageViewHeader>

      <SearchFormContainer>
        <CardHeader>Find Finality Logs</CardHeader>

        <FormRow>
          <ControlContainer>
            <FormControlLabel>Bank Details (Optional)</FormControlLabel>
            <TextField
              id="bank-details-field"
              value={bankDetails}
              onValueChange={(val) => setbankDetails(val)
              } />
          </ControlContainer>

          <ControlContainer>
            <FormControlLabel>Transaction Date (Optional)</FormControlLabel>
            <DateField
              id="transaction-date-field"
              value={transactionDate}
              onValueChange={(val) => setTransactionDate(val)}
            />
          </ControlContainer>

          <ControlContainer>
            <FormControlLabel>Request Date (Optional)</FormControlLabel>
            <DateField
              id="request-date-field"
              value={requestDate}
              onValueChange={(val) => setRequestDate(val)}
            />
          </ControlContainer>

          <ControlContainer>
            <FormControlLabel>Requested By (Optional)</FormControlLabel>
            <TextField
              id="client-field"
              value={client}
              onValueChange={(val) => setClient(val)
              } />
          </ControlContainer>

        </FormRow>

        <ButtonContainer>
          <Button
            id="search-button"
            style={{ width: '120px' }}
            text="Search"
            loading={getFinalityLogsPending}
            onClick={() => searchButtonHandler()}
          />

          <Button
            id="clear-filter-button"
            className="secondary"
            style={{ width: '150px' }}
            text="Clear Filter"
            loading={getFinalityLogsPending}
            onClick={() => clearSearchButtonHandler()}
          />
        </ButtonContainer>

      </SearchFormContainer>

      {getFinalityLogsPending && !getFinalityLogsResponse && (
        <LoadingPageView />
      )}

      {!getFinalityLogsPending && getFinalityLogsError && !getFinalityLogsResponse && (
        <ErrorPageView onRetry={() => fetchFinalityLogs()} />
      )}

      {getFinalityLogsResponse && getFinalityLogsResponse.content.length === 0 && (
        <p>No logs found</p>
      )}

      {getFinalityLogsResponse && getFinalityLogsResponse.content.length > 0 && (
        <>
          <ContentContainer>
            <CardContent style={{ padding: '0px' }}>
              <TableContainer>
                <LoadingOverlay show={getFinalityLogsPending && !!getFinalityLogsResponse} />

                <Table id="finality-logs-table">
                  <thead>
                    <tr>
                      <td>Bank Details</td>
                      <td>Transaction Date</td>
                      <td>Request Date</td>
                      <td className="break-word owner-column">Requested By</td>
                      <td className="center">Response Code</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {getFinalityLogsResponse.content.map((finalityLog) => {
                      const {
                        bankDetails, transactionDate, requestTime, client, responseCode
                      } = finalityLog;

                      const transactionDateObject = new Date(transactionDate);
                      const transactionDateString = formatDate(transactionDateObject, 'dd-MM-yyyy');
                      const transactionTimeString = formatDate(transactionDateObject, 'hh:mm aa');

                      const requestTimeDateObject = new Date(requestTime);
                      const requestDateString = formatDate(requestTimeDateObject, 'dd-MM-yyyy');
                      const requestTimeString = formatDate(requestTimeDateObject, 'hh:mm aa');

                      return (
                        <tr >
                          <td>
                            <p>{bankDetails}</p>
                          </td>

                          <td>
                            <p>{transactionDateString}</p>
                            <p>{transactionTimeString}</p>
                          </td>

                          <td>
                            <p>{requestDateString}</p>
                            <p>{requestTimeString}</p>
                          </td>

                          <td className="break-word owner-column">
                            {client}
                          </td>

                          <td className="center">{responseCode}</td>

                          <td>
                            <a href="#" onClick={() => viewFinalityLogDetails(finalityLog)}>Details</a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableContainer>

              <TableControlsContainer>
                <PageDetails>
                  <p>
                    {`${(currentPage - 1) * PAGE_SIZE + 1}`
                      + ' - '
                      + Math.min((currentPage) * PAGE_SIZE, getFinalityLogsResponse.count)
                      + ' of '
                      + getFinalityLogsResponse.count + ' record(s)'}
                  </p>
                </PageDetails>

                <Paginator
                  id="table-paginator"
                  pageCount={Math.ceil(getFinalityLogsResponse.count / PAGE_SIZE)}
                  currentPage={currentPage}
                  onPageButtonClick={(pageNum) => fetchFinalityLogs(pageNum)}
                />
              </TableControlsContainer>

            </CardContent>
          </ContentContainer>
        </>
      )}

    </Container>
  );
}


const mapStateToProps = (state: AppState): StoreStateProps => ({
  getFinalityLogsPending: state.getFinalityLogs.getFinalityLogsPending,
  getFinalityLogsError: state.getFinalityLogs.getFinalityLogsError,
  getFinalityLogsResponse: state.getFinalityLogs.getFinalityLogsResponse,
});

const mapDispatchToProps = (
  dispatch: (action: any) => void
): StoreDispatchProps => ({
  getFinalityLogs(request: GetFinalityLogsRequest) {
    dispatch(triggerGetFinalityLogs(request));
  },
  resetGetFinalityLogs() {
    dispatch(resetGetFinalityLogs());
  },
  displayToast(toastPayload: TriggerDisplayToastPayload) {
    dispatch(triggerDisplayToast(toastPayload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(FinalityLogsPage);